import React, { useMemo, useState } from "react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { formatNumber } from "../../../../shared/utils/numberFormatter";
import { makeFormatValue } from "../utils/axisFormat";

/**
 * RetentionCohortTable - Shows cohort matrix (rows = cohorts, columns = intervals)
 * Mirrors FunnelDataTable structure
 */
export default function RetentionCohortTable({
  intervals = [],
  cohorts = [],
  weightedAverage = null,
  breakdowns = [],
  metric = "retention_rate",
  config = {},
}) {
  const leftY = config?.axis?.leftY ?? {};
  const yAxisConfig = leftY;
  const formatYValue = makeFormatValue(yAxisConfig);

  const hasBreakdowns = Array.isArray(breakdowns) && breakdowns.length > 0;
  const [expandedSegments, setExpandedSegments] = useState(() => ({}));

  const weightedAverageRow =
    weightedAverage && Array.isArray(weightedAverage.values) && weightedAverage.values.length > 0
      ? {
          label: "Weighted Average",
          cohortSize: "100%",
          values: intervals.map((_, idx) => weightedAverage.values[idx] ?? 0),
          isIncomplete: intervals.map((_, idx) =>
            Array.isArray(weightedAverage.isComplete) ? weightedAverage.isComplete[idx] === false : false
          ),
          isSummary: true,
        }
      : null;

  const tableRows = weightedAverageRow ? [weightedAverageRow, ...cohorts] : cohorts;
  const breakdownRows = useMemo(() => {
    if (!hasBreakdowns) return [];
    return breakdowns.map((bd, index) => {
      const segmentLabel =
        bd?.segment != null && String(bd.segment).trim() !== "" ? String(bd.segment) : "(none)";
      const values = Array.isArray(bd?.values)
        ? intervals.map((_, idx) => bd.values[idx] ?? 0)
        : new Array(intervals.length).fill(0);
      const isIncomplete = Array.isArray(bd?.isComplete)
        ? intervals.map((_, idx) => bd.isComplete[idx] !== true)
        : new Array(intervals.length).fill(false);
      const childRows = (Array.isArray(bd?.cohorts) ? bd.cohorts : []).map((c) => {
        const cohortSize = Number(c?.cohortSize) || 0;
        const retained = Array.isArray(c?.retained) ? c.retained : [];
        const childIncomplete = Array.isArray(c?.isComplete)
          ? intervals.map((_, idx) => c.isComplete[idx] !== true)
          : new Array(intervals.length).fill(false);
        const childValues = intervals.map((_, idx) => {
          const retainedUsers = Number(retained[idx]) || 0;
          if (metric === "retention_rate") {
            return cohortSize > 0 ? (retainedUsers / cohortSize) * 100 : 0;
          }
          return retainedUsers;
        });
        return {
          label: c?.label || "Unknown",
          cohortSize,
          values: childValues,
          isIncomplete: childIncomplete,
          isChild: true,
        };
      });
      return {
        id: `segment-${index}`,
        label: segmentLabel,
        cohortSize: "100%",
        values,
        isIncomplete,
        children: childRows,
        isParent: true,
      };
    });
  }, [breakdowns, hasBreakdowns, intervals, metric]);

  const toggleSegment = (segmentId) => {
    setExpandedSegments((prev) => ({ ...prev, [segmentId]: !prev[segmentId] }));
  };

  if (intervals.length === 0 || (!hasBreakdowns && cohorts.length === 0)) {
    return (
      <div className="w-full p-4 text-center text-sm" style={{ color: theme.textMuted }}>
        No data available
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse" style={{ fontFamily: "Raleway, sans-serif" }}>
        <thead style={{ backgroundColor: "#fafafa" }}>
          <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
            <th
              className="text-left py-2 px-3 text-xs font-semibold uppercase whitespace-nowrap"
              style={{ color: theme.textMuted, borderRight: `1px solid ${theme.border}` }}
            >
              Date
            </th>
            <th
              className="text-left py-2 px-3 text-xs font-semibold uppercase whitespace-nowrap"
              style={{ color: theme.textMuted, borderRight: `1px solid ${theme.border}` }}
            >
              Total Profile(s)
            </th>
            {intervals.map((interval, index) => (
              <th
                key={index}
                className="text-left py-2 px-3 text-xs font-semibold uppercase whitespace-nowrap"
                style={{
                  color: theme.textMuted,
                  borderRight: index < intervals.length - 1 ? `1px solid ${theme.border}` : "none",
                  minWidth: 56,
                }}
              >
                {interval}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hasBreakdowns
            ? breakdownRows.map((segment, rowIndex) => (
                <React.Fragment key={segment.id}>
                  <tr
                    style={{
                      borderBottom: `1px solid ${theme.border}`,
                      backgroundColor: rowIndex % 2 === 0 ? "white" : "#fafafa",
                    }}
                  >
                    <td
                      className="py-2 px-3 text-sm whitespace-nowrap"
                      style={{
                        color: theme.text,
                        borderRight: `1px solid ${theme.border}`,
                        fontWeight: 500,
                        minWidth: 96,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSegment(segment.id)}
                        className="inline-flex items-center gap-2"
                        style={{ color: theme.text, background: "transparent", border: "none", padding: 0 }}
                      >
                        <span style={{ width: 12, display: "inline-block", textAlign: "center" }}>
                          {expandedSegments[segment.id] ? "▾" : "▸"}
                        </span>
                        <span>{segment.label}</span>
                      </button>
                    </td>
                    <td
                      className="py-2 px-3 text-sm text-right"
                      style={{
                        color: theme.text,
                        borderRight: `1px solid ${theme.border}`,
                        fontWeight: 600,
                      }}
                    >
                      {segment.cohortSize}
                    </td>
                    {intervals.map((_, colIndex) => {
                      const value = segment.values[colIndex] ?? 0;
                      const isIncomplete = segment.isIncomplete[colIndex] === true;
                      const formattedValue = metric === "retention_rate"
                        ? `${formatNumber(value, { decimals: 2 })}%`
                        : formatNumber(value);
                      return (
                        <td
                          key={`${segment.id}-${colIndex}`}
                          className="py-2 px-3 text-sm text-right"
                          style={{
                            color: theme.text,
                            borderRight: colIndex < intervals.length - 1 ? `1px solid ${theme.border}` : "none",
                          }}
                        >
                          {formattedValue}{isIncomplete ? "*" : ""}
                        </td>
                      );
                    })}
                  </tr>
                  {expandedSegments[segment.id] && segment.children.map((cohortRow, childIndex) => (
                    <tr
                      key={`${segment.id}-child-${childIndex}`}
                      style={{
                        borderBottom: `1px solid ${theme.border}`,
                        backgroundColor: "#fcfcfd",
                      }}
                    >
                      <td
                        className="py-2 px-3 text-sm whitespace-nowrap"
                        style={{
                          color: theme.textMuted,
                          borderRight: `1px solid ${theme.border}`,
                          minWidth: 96,
                          paddingLeft: 28,
                        }}
                      >
                        {cohortRow.label}
                      </td>
                      <td
                        className="py-2 px-3 text-sm text-right"
                        style={{
                          color: theme.textMuted,
                          borderRight: `1px solid ${theme.border}`,
                        }}
                      >
                        {formatNumber(cohortRow.cohortSize || 0)}
                      </td>
                      {intervals.map((_, colIndex) => {
                        const value = cohortRow.values[colIndex] ?? 0;
                        const isIncomplete = cohortRow.isIncomplete[colIndex] === true;
                        const formattedValue = metric === "retention_rate"
                          ? `${formatNumber(value, { decimals: 2 })}%`
                          : formatNumber(value);
                        return (
                          <td
                            key={`${segment.id}-child-${childIndex}-${colIndex}`}
                            className="py-2 px-3 text-sm text-right"
                            style={{
                              color: theme.textMuted,
                              borderRight: colIndex < intervals.length - 1 ? `1px solid ${theme.border}` : "none",
                            }}
                          >
                            {formattedValue}{isIncomplete ? "*" : ""}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))
            : tableRows.map((cohort, rowIndex) => (
                <tr
                  key={rowIndex}
                  style={{
                    borderBottom: `1px solid ${theme.border}`,
                    backgroundColor: rowIndex % 2 === 0 ? "white" : "#fafafa",
                  }}
                >
                  <td
                    className="py-2 px-3 text-sm whitespace-nowrap"
                    style={{
                      color: theme.text,
                      borderRight: `1px solid ${theme.border}`,
                      fontWeight: 500,
                      minWidth: 96,
                    }}
                  >
                    {cohort.label || "Unknown"}
                  </td>
                  <td
                    className="py-2 px-3 text-sm text-right"
                    style={{
                      color: theme.text,
                      borderRight: `1px solid ${theme.border}`,
                      fontWeight: cohort.isSummary ? 600 : 400,
                    }}
                  >
                    {typeof cohort.cohortSize === "string"
                      ? cohort.cohortSize
                      : formatNumber(cohort.cohortSize || 0)}
                  </td>
                  {intervals.map((interval, colIndex) => {
                    const value = cohort.values && cohort.values[colIndex] !== undefined
                      ? cohort.values[colIndex]
                      : 0;
                    const isIncomplete = Array.isArray(cohort.isIncomplete) && cohort.isIncomplete[colIndex] === true;

                    const formattedValue = metric === "retention_rate"
                      ? `${formatNumber(value, { decimals: 2 })}%`
                      : formatNumber(value);

                    return (
                      <td
                        key={colIndex}
                        className="py-2 px-3 text-sm text-right"
                        style={{
                          color: theme.text,
                          borderRight: colIndex < intervals.length - 1 ? `1px solid ${theme.border}` : "none",
                        }}
                      >
                        {formattedValue}{isIncomplete ? "*" : ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
